import correctSequence from './correctSoundSequence.json';

const SOUND_STORAGE_KEY = 'mini_tasks_sound_enabled';

let audioContext = null;

export function readMiniTaskSoundEnabled() {
  try {
    return window.localStorage.getItem(SOUND_STORAGE_KEY) !== '0';
  } catch (_err) {
    return true;
  }
}

export function writeMiniTaskSoundEnabled(enabled) {
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, enabled ? '1' : '0');
  } catch (_err) {
    // Ignore storage errors. Sound can still work for the current session.
  }
}

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioContext) audioContext = new AudioContextClass();
  return audioContext;
}

export function prepareMiniTaskSound(enabled = true) {
  if (!enabled) return Promise.resolve(null);
  const context = getAudioContext();
  if (!context || context.state === 'closed') return Promise.resolve(null);
  if (context.state === 'running') return Promise.resolve(context);
  if (typeof context.resume !== 'function') return Promise.resolve(null);
  return context.resume()
    .then(() => (context.state === 'running' ? context : null))
    .catch(() => null);
}

function playTone(context, output, { at, frequency, duration, gain = 0.075, type = 'sine' }) {
  const oscillator = context.createOscillator();
  const volume = context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, at);
  volume.gain.setValueAtTime(0.0001, at);
  volume.gain.exponentialRampToValueAtTime(gain, at + 0.018);
  volume.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  oscillator.connect(volume);
  volume.connect(output);
  oscillator.start(at);
  oscillator.stop(at + duration + 0.02);
}

function stereoOutput(context, pan) {
  if (!Number.isFinite(pan) || pan === 0 || typeof context.createStereoPanner !== 'function') {
    return context.destination;
  }
  const panner = context.createStereoPanner();
  const clampedPan = Math.max(-1, Math.min(1, pan));
  if (typeof panner.pan?.setValueAtTime === 'function') {
    panner.pan.setValueAtTime(clampedPan, context.currentTime);
  } else if (panner.pan) {
    panner.pan.value = clampedPan;
  }
  panner.connect(context.destination);
  return panner;
}

function scheduleSequence(context, sequence, { pan = 0 } = {}) {
  const output = stereoOutput(context, pan);
  let cursor = context.currentTime + 0.01;
  sequence.forEach((tone) => {
    playTone(context, output, { at: cursor, ...tone });
    cursor += tone.step || tone.duration;
  });
}

function playSequence(sequence, options = {}) {
  const context = getAudioContext();
  if (!context || context.state === 'closed') return Promise.resolve(null);
  if (context.state === 'running') {
    scheduleSequence(context, sequence, options);
    return Promise.resolve(context);
  }
  return prepareMiniTaskSound().then((readyContext) => {
    if (!readyContext) return null;
    scheduleSequence(readyContext, sequence, options);
    return readyContext;
  });
}

export function playMiniTaskSound(kind, enabled = true, options = {}) {
  if (!enabled) return;
  if (kind === 'correct') {
    return playSequence(correctSequence, options);
  }
  if (kind === 'wrong') {
    return playSequence([
      { frequency: 246.94, duration: 0.11, step: 0.1, type: 'sine', gain: 0.045 },
      { frequency: 196.0, duration: 0.18, step: 0.16, type: 'sine', gain: 0.04 },
    ], options);
  }
  if (kind === 'complete') {
    return playSequence([
      { frequency: 523.25, duration: 0.08, step: 0.075, type: 'triangle', gain: 0.065 },
      { frequency: 659.25, duration: 0.08, step: 0.075, type: 'triangle', gain: 0.07 },
      { frequency: 783.99, duration: 0.09, step: 0.085, type: 'triangle', gain: 0.075 },
      { frequency: 1046.5, duration: 0.22, step: 0.18, type: 'triangle', gain: 0.07 },
    ], options);
  }
  if (kind === 'advance') {
    return playSequence([
      { frequency: 523.25, duration: 0.14, step: 0.11, type: 'triangle', gain: 0.06 },
      { frequency: 659.25, duration: 0.16, step: 0.13, type: 'triangle', gain: 0.065 },
      { frequency: 783.99, duration: 0.18, step: 0.15, type: 'triangle', gain: 0.07 },
      { frequency: 1046.5, duration: 0.22, step: 0.18, type: 'triangle', gain: 0.07 },
      { frequency: 1318.51, duration: 0.42, step: 0.36, type: 'triangle', gain: 0.065 },
    ], options);
  }
  if (kind === 'champion') {
    return playSequence([
      { frequency: 392.0, duration: 0.2, step: 0.14, type: 'triangle', gain: 0.05 },
      { frequency: 523.25, duration: 0.2, step: 0.14, type: 'triangle', gain: 0.055 },
      { frequency: 659.25, duration: 0.24, step: 0.2, type: 'triangle', gain: 0.06 },
      { frequency: 523.25, duration: 0.16, step: 0.13, type: 'triangle', gain: 0.05 },
      { frequency: 659.25, duration: 0.18, step: 0.14, type: 'triangle', gain: 0.055 },
      { frequency: 783.99, duration: 0.2, step: 0.16, type: 'triangle', gain: 0.06 },
      { frequency: 1046.5, duration: 0.58, step: 0.5, type: 'triangle', gain: 0.07 },
    ], options);
  }
}
